import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { groupBy, toPairs, uniqBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { MemberType, ProjectId, RoleType } from 'api/models';
import { ProjectAPI } from 'api/project';
import { TeamsAPI } from 'api/teams';
import { GENERAL_API_STALE } from 'config/query-config';
import { queryClient as queryClientDefault } from 'queries';
import { getProjectMembersExclusionQueryKey } from 'queries/budget-members-keys';
import { logger } from 'services';
import { addProjectPlaceholder, removeProjectPlaceholder, updateProjectPlaceholder } from 'stores/memberships';
import { addTeam } from 'utils/idb-utils';
import { MEMBERS_V2_MODIFIED_QUERY_KEY, MEMBERS_V2_QUERY_KEY } from './members';
export const getCreatePlaceholderPayload = ({ name, role, budget }, options)=>{
    const isCustomerRole = Number(role.value) === -1 || role.extra.type === RoleType.Customer;
    const budgetIds = budget && budget.length > 0 ? budget.map((option)=>option.value).filter((id)=>id != null) : undefined;
    return {
        teamName: name,
        ...((options === null || options === void 0 ? void 0 : options.setCustomerRole) || Number(role.value) !== -1) && {
            roleId: Number(role.value)
        },
        teamType: isCustomerRole ? MemberType.External : MemberType.Native,
        ...budgetIds && budgetIds.length > 0 && {
            projectFinancialsBudgetIds: budgetIds
        }
    };
};
export const createProjectPlaceholderMutationFn = (projectId, teamSource)=>async (form)=>{
        const team = await TeamsAPI.createProjectTeam({
            projectId,
            ...getCreatePlaceholderPayload(form),
            teamSource
        });
        if (team) {
            const data = queryClientDefault.getQueryData(MEMBERS_V2_QUERY_KEY);
            team.projectIds = [
                projectId
            ];
            if (data === null || data === void 0 ? void 0 : data.teams) {
                data.teams[team.teamId] = team;
            }
            queryClientDefault.setQueryData(MEMBERS_V2_QUERY_KEY, data);
            queryClientDefault.refetchQueries(MEMBERS_V2_MODIFIED_QUERY_KEY);
            queryClientDefault.invalidateQueries(getProjectMembersExclusionQueryKey(ProjectId(projectId)));
            addTeam({
                ...team
            }).catch((error)=>{
                logger.error(`Error occured while updating placeholder insider indexedDB teamId: ${team === null || team === void 0 ? void 0 : team.teamId} projectId:${projectId}`, error);
            });
        }
        return team;
    };
const createMutationFn = (formValues)=>{
    return TeamsAPI.createTeam(getCreatePlaceholderPayload(formValues));
};
const updateMutationFn = (id, { name, role })=>{
    return TeamsAPI.updateTeam(id, {
        teamName: name,
        ...role && {
            roleId: Number(role.value)
        }
    });
};
export const useUpdatePlaceholderMutation = (id, options = {})=>useMutation((formValues)=>updateMutationFn(id, formValues), options);
const singleAssignMutationFn = (projectId, teamId, userId, roleId)=>{
    /**
   * Teams PUT API is throwing 500, for single placeholder assignment,
   * hence using BULK API for the same, passing an array with single entry.
   */ return TeamsAPI.bulkAssignPlaceholder([
        {
            projectId,
            teamId,
            roleId,
            userList: [
                {
                    userId
                }
            ]
        }
    ]);
};
const bulkAssignMutationFn = (projectId, placeholderOptions)=>{
    const payload = [];
    placeholderOptions.forEach(({ option: _placeholder, roleId })=>{
        var _placeholder_user_extra, _placeholder_user, _placeholder_user_extra1, _placeholder_user1, _placeholder_user_extra2, _placeholder_user2, _placeholder_user3, _placeholder_extra;
        payload.push({
            projectId,
            userList: ((_placeholder_user = _placeholder.user) === null || _placeholder_user === void 0 ? void 0 : (_placeholder_user_extra = _placeholder_user.extra) === null || _placeholder_user_extra === void 0 ? void 0 : _placeholder_user_extra.type) === 'user' ? [
                {
                    userId: Number((_placeholder_user1 = _placeholder.user) === null || _placeholder_user1 === void 0 ? void 0 : (_placeholder_user_extra1 = _placeholder_user1.extra) === null || _placeholder_user_extra1 === void 0 ? void 0 : _placeholder_user_extra1.id)
                }
            ] : undefined,
            companyList: ((_placeholder_user2 = _placeholder.user) === null || _placeholder_user2 === void 0 ? void 0 : (_placeholder_user_extra2 = _placeholder_user2.extra) === null || _placeholder_user_extra2 === void 0 ? void 0 : _placeholder_user_extra2.type) === 'company' ? [
                {
                    companyId: Number((_placeholder_user3 = _placeholder.user) === null || _placeholder_user3 === void 0 ? void 0 : _placeholder_user3.extra.id)
                }
            ] : undefined,
            roleId,
            teamId: (_placeholder_extra = _placeholder.extra) === null || _placeholder_extra === void 0 ? void 0 : _placeholder_extra.id
        });
    });
    return TeamsAPI.bulkAssignPlaceholder(payload);
};
export const useMultiAssignPlaceholderMutation = (projectId, options = {})=>useMutation((placeholderOptions)=>bulkAssignMutationFn(projectId, placeholderOptions), options);
export const useSingleAssignPlaceholderMutation = (projectId, teamId, userId, options = {})=>useMutation((roleId)=>singleAssignMutationFn(projectId, teamId, userId, roleId), options);
export const useCreatePlaceholderMutation = (options = {})=>useMutation(createMutationFn, options);
export const getProjectPlaceholdersQueryKey = ([, projectId])=>[
        'placeholders',
        projectId
    ];
export const getPlaceholderResolutionByProjectQueryKey = ([, projectId])=>[
        'placeholder_resolution',
        projectId
    ];
export const getPlaceholderResolutionByProjectQueryKeyV2 = ([, projectId])=>[
        'placeholder_resolution_v2',
        projectId
    ];
export const getProjectPlaceholdersWithMetaQueryKey = ([, projectId])=>[
        'placeholders',
        'with-meta',
        projectId
    ];
export const usePlaceholdersByProjectQuery = (projectId, queryOptions = {})=>useQuery(getProjectPlaceholdersQueryKey(projectId), ()=>TeamsAPI.getProjectPlaceholders(projectId), {
        staleTime: GENERAL_API_STALE,
        ...queryOptions
    });
export const usePlaceholdersWithMetaByProjectQuery = (projectId, queryOptions = {})=>useQuery(getProjectPlaceholdersWithMetaQueryKey(projectId), ()=>TeamsAPI.getProjectPlaceholdersWithMeta(projectId), {
        staleTime: GENERAL_API_STALE,
        ...queryOptions
    });
const deleteMutationFn = (id)=>TeamsAPI.deleteTeam(id);
export const useDeletePlaceholderMutation = (id, options = {})=>useMutation(()=>deleteMutationFn(id), options);
export const useDeletePlaceholdersByRoleMutation = (options = {})=>useMutation(({ roleId })=>TeamsAPI.deleteTeamsByRole(roleId), options);
export const getTeamsInAccountQueryKey = ()=>[
        'teams',
        'all'
    ];
export const useAllTeamsInAccount = (queryOptions = {})=>useQuery(getTeamsInAccountQueryKey(), ()=>TeamsAPI.getAllTeamsInAccount(), {
        staleTime: GENERAL_API_STALE,
        select: (teams)=>uniqBy(teams, 'teamId'),
        ...queryOptions
    });
export const useNativeTeamsInAccount = ()=>{
    const { data, ...rest } = useAllTeamsInAccount();
    const nativeTeams = useMemo(()=>data === null || data === void 0 ? void 0 : data.filter((item)=>item.teamType !== MemberType.External).map(({ userList, companyList, ...restOfProps })=>restOfProps), [
        data
    ]);
    return {
        data: nativeTeams,
        ...rest
    };
};
export const useDefaultTeamsInAccount = ()=>{
    const { data, ...rest } = useAllTeamsInAccount();
    return {
        data: data === null || data === void 0 ? void 0 : data.filter((item)=>!item.projectId),
        ...rest
    };
};
export const useDefaultNativeTeamsInAccount = ()=>{
    const { data, ...rest } = useNativeTeamsInAccount();
    return {
        data: data === null || data === void 0 ? void 0 : data.filter((item)=>!item.projectId),
        ...rest
    };
};
export const useUpdateTeamNameMutation = (options = {})=>useMutation(({ id, name })=>updateMutationFn(id, {
            name
        }), options);
export const useAssignUserToTeamMutation = (options = {})=>useMutation(({ projectId, teamId, userId, roleId })=>singleAssignMutationFn(projectId, teamId, userId, roleId), options);
export const useAddTeamToLocalState = ()=>{
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useCallback((projectId, team)=>{
        queryClient.setQueryData(getTeamsInAccountQueryKey(), (oldValues = [])=>team.projectId != null ? [
                ...oldValues.filter(({ teamId })=>teamId !== team.teamId),
                team
            ] : oldValues);
        dispatch(addProjectPlaceholder({
            projectId: ProjectId(projectId),
            team
        }));
    }, [
        dispatch,
        queryClient
    ]);
};
export const useRemoveTeamFromLocalState = ()=>{
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useCallback((projectId, teamId)=>{
        queryClient.setQueryData(getTeamsInAccountQueryKey(), (oldValues = [])=>oldValues.filter((team)=>team.projectId != null ? team.teamId !== teamId : true));
        dispatch(removeProjectPlaceholder({
            projectId: ProjectId(projectId),
            teamId
        }));
    }, [
        dispatch,
        queryClient
    ]);
};
export const PlaceholderResolutionsQueryKey = [
    'placeholder_resolution'
];
export const useChangeTeamResolvedStateInLocalState = ()=>{
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useCallback((projectId, teamId, resolved)=>{
        const updatedTeams = queryClient.setQueryData(getTeamsInAccountQueryKey(), (oldValues = [])=>oldValues.map((team)=>team.projectId === projectId && team.teamId === teamId ? {
                    ...team,
                    resolved
                } : team));
        const updatedTeam = updatedTeams === null || updatedTeams === void 0 ? void 0 : updatedTeams.find((team)=>team.projectId === projectId && team.teamId === teamId);
        if (updatedTeam != null) {
            dispatch(updateProjectPlaceholder({
                projectId: ProjectId(projectId),
                team: updatedTeam
            }));
        }
        queryClient.invalidateQueries(PlaceholderResolutionsQueryKey);
    }, [
        dispatch,
        queryClient
    ]);
};
export const useUnresolveTeamInLocalState = ()=>{
    const handler = useChangeTeamResolvedStateInLocalState();
    return useCallback((projectId, teamId)=>{
        handler(projectId, teamId, false);
    }, [
        handler
    ]);
};
export const useResolveTeamInLocalState = ()=>{
    const handler = useChangeTeamResolvedStateInLocalState();
    return useCallback((projectId, teamId, userId)=>{
        handler(projectId, teamId, true, userId);
    }, [
        handler
    ]);
};
// deprecated
export const usePlaceholderResolutionByProjectQuery = (projectId, queryOptions = {})=>useQuery(getPlaceholderResolutionByProjectQueryKey(projectId), async ()=>{
        const { mappings } = await ProjectAPI.getPlaceholderResolution(projectId);
        /**
       * placeholder can always be resolved to a single user
       * but the same user can be used to resolve multiple placeholders
       * hence to have a uniform type, array of ids is used
       * for both user and team mapping.
       */ const userMap = {};
        /**
       * adding both userId and teamId as key and value
       * to facilitate easier lookup using both keys.
       */ const teamMap = mappings.reduce((result, _mapping)=>{
            var _mapping_company, _mapping_company1;
            const key = _mapping.user != null ? `user_${_mapping.user.userId}` : `company_${(_mapping_company = _mapping.company) === null || _mapping_company === void 0 ? void 0 : _mapping_company.companyId}`;
            userMap[key] = [
                ...userMap[key] || [],
                _mapping.teamId
            ];
            result[`team_${_mapping.teamId}`] = _mapping.user != null ? [
                _mapping.user.userId
            ] : [
                (_mapping_company1 = _mapping.company) === null || _mapping_company1 === void 0 ? void 0 : _mapping_company1.companyId
            ];
            return result;
        }, {});
        return {
            ...userMap,
            ...teamMap
        };
    }, {
        staleTime: GENERAL_API_STALE,
        refetchOnMount: true,
        ...queryOptions
    });
export const InitialPlaceholderResolutionV2 = {
    resolvedWithEntities: {},
    mappings: {},
    resolvedWithTaskIds: {}
};
const getPlaceholderResolutionMappingFromResponse = (mappings)=>{
    return mappings.reduce((result, placeholderMapping)=>{
        if (placeholderMapping.user != null) {
            var _result_resolvedWithEntities_, _result_resolvedWithTaskIds_;
            const existingMappedPlaceholdersForUser = (_result_resolvedWithEntities_ = result.resolvedWithEntities[`user_${placeholderMapping.user.userId}`]) !== null && _result_resolvedWithEntities_ !== void 0 ? _result_resolvedWithEntities_ : [];
            const existingMappedPlaceholdersForUserWithTeamInfo = (_result_resolvedWithTaskIds_ = result.resolvedWithTaskIds[`user_${placeholderMapping.user.userId}`]) !== null && _result_resolvedWithTaskIds_ !== void 0 ? _result_resolvedWithTaskIds_ : [];
            return {
                resolvedWithEntities: {
                    ...result.resolvedWithEntities,
                    [`user_${placeholderMapping.user.userId}`]: existingMappedPlaceholdersForUser.concat(placeholderMapping.teamId)
                },
                mappings: {
                    ...result.mappings,
                    [placeholderMapping.teamId]: {
                        identityType: 'USER',
                        userId: placeholderMapping.user.userId,
                        budgetIds: placeholderMapping.budgetIds,
                        roleId: placeholderMapping.roleId
                    }
                },
                resolvedWithTaskIds: {
                    ...result.resolvedWithTaskIds,
                    [`user_${placeholderMapping.user.userId}`]: existingMappedPlaceholdersForUserWithTeamInfo.concat({
                        teamId: placeholderMapping.teamId,
                        taskIds: placeholderMapping.taskIds,
                        roleId: placeholderMapping.roleId
                    })
                }
            };
        }
        if (placeholderMapping.company != null) {
            var _result_resolvedWithEntities_1;
            const existingMappedPlaceholdersForUser = (_result_resolvedWithEntities_1 = result.resolvedWithEntities[`company_${placeholderMapping.company.companyId}`]) !== null && _result_resolvedWithEntities_1 !== void 0 ? _result_resolvedWithEntities_1 : [];
            return {
                ...result,
                resolvedWithEntities: {
                    ...result.resolvedWithEntities,
                    [`company_${placeholderMapping.company.companyId}`]: existingMappedPlaceholdersForUser.concat(placeholderMapping.teamId)
                },
                mappings: {
                    ...result.mappings,
                    [placeholderMapping.teamId]: {
                        identityType: 'COMPANY',
                        companyId: placeholderMapping.company.companyId,
                        budgetIds: placeholderMapping.budgetIds,
                        roleId: placeholderMapping.roleId
                    }
                }
            };
        }
        return result;
    }, InitialPlaceholderResolutionV2);
};
export const usePlaceholderResolutionByProjectQueryV2 = (projectId, queryOptions = {})=>useQuery(getPlaceholderResolutionByProjectQueryKeyV2(projectId), async ()=>{
        const { mappings } = await ProjectAPI.getPlaceholderResolution(projectId);
        return getPlaceholderResolutionMappingFromResponse(mappings);
    }, {
        staleTime: GENERAL_API_STALE,
        refetchOnMount: true,
        ...queryOptions
    });
export const usePlaceholderResolutionsQuery = (queryOptions = {})=>useQuery(PlaceholderResolutionsQueryKey, async ()=>{
        const { mappings } = await ProjectAPI.getAllProjectsPlaceholderResolution();
        return toPairs(groupBy(mappings, 'projectId')).reduce((result, [projectId, teamMappings])=>{
            result[projectId] = getPlaceholderResolutionMappingFromResponse(teamMappings);
            return result;
        }, {});
    }, {
        staleTime: GENERAL_API_STALE,
        refetchOnMount: true,
        ...queryOptions
    });
